import { perfLog } from '../../utils/perf';
import { isHubOnly } from '../../utils/hub-runtime';
import { toplog } from '../toplog';
import { dataContext } from '../../FlowSync/context';
import type { OutputChunk } from '../../pty-sync/types.js';
import type { OrphanEntry } from './ptyOrphanBuffer';

export type PtyOutputListener = (data: string, seq?: number) => void;
export type PtyLineListener = (line: string) => void;
/** Raw decoded output, escapes included — every chunk, attached or not. */
export type PtyTextListener = (text: string) => void;
/**
 * Pattern watcher registered via ``Shell.addTrigger`` /
 * ``PtyConnection.addTrigger``. ``onMatch`` fires when an ANSI-stripped PTY
 * line matches ``pattern``. ``label`` is shown in the PTY Events Viewer; if
 * absent, the viewer falls back to ``pattern.toString()``.
 */
export type PtyEvent = {
  pattern: RegExp;
  onMatch: (line: string, match: RegExpMatchArray) => void;
  label?: string;
};

/** A single recorded fire of a registered ``PtyEvent`` — buffered in
 *  ``PtyConnection`` and surfaced in the PTY Events Viewer. */
export interface PtyEventFire {
  /** Local uuid for stable React keys. */
  id: string;
  /** ``Date.now()`` at fire time. */
  ts: number;
  /** ``pattern.toString()`` — the regex literal source. */
  patternSource: string;
  /** ``PtyEvent.label`` if set by the caller. */
  label?: string;
  /** ANSI-stripped line that matched. */
  line: string;
  /** First 8 capture groups (or fewer) from the match — slice protects the buffer. */
  match: string[];
  /** True iff the fire happened during the replay phase (pre-attach). */
  duringReplay: boolean;
}

export type PtyEventFireListener = (fire: PtyEventFire) => void;

export type PtyConnectionStatus = 'idle' | 'connecting' | 'live' | 'restarting' | 'closed';

/** Strip CSI/SGR escape sequences so regex matchers see plain text.
 *
 * Matches the most common ANSI control sequences emitted by terminal
 * applications: CSI (`ESC [ ... letter`), OSC (`ESC ] ... BEL/ST`), and
 * single-character escapes. Not exhaustive — bracketed paste mode and
 * unusual DCS sequences may slip through — but covers >99% of real PTY
 * output Claude Code / shell prompts produce.
 */
const ANSI_RE = /\x1b(?:\[[0-?]*[ -/]*[@-~]|\][^\x07\x1b]*(?:\x07|\x1b\\)|[@-Z\\-_])/g;
export function stripAnsi(s: string): string {
  return s.replace(ANSI_RE, '');
}

/** Decode a base64 string into raw bytes (PTY chunks travel base64 over WS/REST). */
export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export class PtyConnection {
  shellId: string;
  computeNodeId: string;

  // ── Internal state (moved from Shell) ────────────────────────────────────
  started = false;
  restarting = false;
  lastSeq = 0;

  /**
   * Live in-session output chunks keyed by seq — feeds pty-sync (VT rebuild, gutters) and the
   * attach gap (chunks newer than the recording). A replay WINDOW, not the session's history:
   * the oldest entries are dropped once the window exceeds a chunk or byte bound, so a TUI that
   * redraws one line all day (one chunk per frame) costs a fixed amount, not one entry per frame.
   * Older output is the recording's job (pty-stream), never memory's.
   */
  readonly chunks: Map<number, OutputChunk> = new Map();
  /**
   * The window covers the VirtualTerminal's whole scrollback at one chunk per row
   * (rows + 10,000 `scrollbackLines`, see PtySyncSession) with room for cursor-only frames,
   * and ~1.5 MiB of payload. Sized for the replay, not a wait: neither is a timeout or a budget.
   */
  static readonly CHUNK_WINDOW_MAX_CHUNKS = 12_288;
  static readonly CHUNK_WINDOW_MAX_BYTES = 1_572_864;
  private _chunkBytes = 0;
  /** Highest seq dropped from the window; 0 while nothing has been dropped. */
  trimmedThroughSeq = 0;

  private readonly decoder = new TextDecoder('utf-8', { fatal: false });

  /** Live output listeners — only notified after isReady. */
  private readonly _listeners = new Set<PtyOutputListener>();

  /** Line listeners — fed by every chunk (including replay). ANSI-stripped. */
  private readonly _lineListeners = new Set<PtyLineListener>();

  /** Active triggers — pattern + onMatch. Wrapped over the line stream. */
  private readonly _triggers = new Set<PtyEvent>();

  /** Bounded ring of fire records — surfaced in PTY Events Viewer. */
  private readonly _eventFires: PtyEventFire[] = [];
  private readonly _eventFireListeners = new Set<PtyEventFireListener>();
  private static readonly MAX_EVENT_FIRES = 200;

  /** Pending raw text not yet terminated by LF or a terminal-row CR. */
  private _lineBuffer = '';

  /** Raw text listeners — the decoded stream as it arrives, escapes and all. */
  private readonly _textListeners = new Set<PtyTextListener>();

  /** onReady subscribers — fired once when attach completes + live stream opens. */
  private readonly _readyListeners = new Set<() => void>();

  /** onDisconnect subscribers — fired when WS closes. */
  private readonly _disconnectListeners = new Set<() => void>();

  /** True once attach() has completed and the output gate is open. */
  private _attached = false;
  // `pty` toplog: log the first line of a run of dropped chunks / inputs only.
  private _traceDedupRun = false;
  private _traceInputDropRun = false;

  /**
   * Keystrokes typed before this connection was EVER ready — a real terminal's
   * typeahead. A view mounts its xterm before the first attach completes, and
   * what a person types in that gap belongs to the shell, not the floor. Only
   * the first attach flushes it: input into a pane that WAS live and then
   * dropped is still refused, so a dead pane never replays stale keys later.
   */
  private _typeahead = '';
  private _everReady = false;

  /** Backend PTY ID currently attached in this browser client. */
  private _attachedPtyId: string | null = null;

  /**
   * The size of the view showing this PTY on screen, or null while none is.
   * A PTY has ONE winsize shared by every client attached to it, so whoever
   * sizes it last wins — kept here, never dropped, it is what this client
   * asserts on every attach (and re-sends after one), not a window-sized guess.
   * A hidden view releases it, so it never resizes a PTY nobody is looking at.
   */
  private _viewSize: { cols: number; rows: number } | null = null;

  /** In-flight attach dedup guard. */
  private _attachPromise: Promise<void> | null = null;

  /** PTY ID currently being attached. */
  private _attachingPtyId: string | null = null;

  constructor(shellId = '', computeNodeId = '') {
    this.shellId = shellId;
    this.computeNodeId = computeNodeId;
  }

  // ── Status ────────────────────────────────────────────────────────────────

  /** True once attach() has completed (no WS dependency — safe in unit tests). */
  get attached(): boolean {
    return this._attached;
  }

  /** True if this connection is fully attached to the given PTY. */
  isAttachedTo(ptyId: string): boolean {
    return this.started && this._attached && this._attachedPtyId === ptyId;
  }

  /**
   * True when attach is complete AND the WS is live.
   * Equivalent to the old shell.connected.
   */
  get isReady(): boolean {
    return this._attached && this.isLive;
  }

  /** True when the backend PTY is started and WS is live (no replay gate). */
  get isLive(): boolean {
    return this.started && !this.restarting && dataContext.isConnected;
  }

  get status(): PtyConnectionStatus {
    if (!this.started) return this.restarting ? 'restarting' : 'idle';
    if (this.restarting) return 'restarting';
    return 'live';
  }

  // ── Sorted chunk accessor ─────────────────────────────────────────────────

  /** Sorted chunks of the replay window — feeds VirtualTerminal rebuild on resize. */
  getSortedChunks(): OutputChunk[] {
    return [...this.chunks.values()].sort((a, b) => a.seq - b.seq);
  }

  /** Single chunk by seq — for ptySyncRef.processChunk(). */
  getChunk(seq: number): OutputChunk | undefined {
    return this.chunks.get(seq);
  }

  // ── Output routing (called by Shell / DataManager) ────────────────────────

  /**
   * Route a pty_output_msg chunk into this connection.
   * Stores the chunk for pty-sync; notifies live listeners once attached.
   */
  routeOutput(data: string, seq?: number, timestamp_ms?: number): string | null {
    return this.appendOutput(data, seq, timestamp_ms);
  }

  /**
   * Decode and store a base64-encoded PTY output chunk.
   * Returns decoded string, or null if deduped/failed.
   */
  appendOutput(base64Data: string, seq?: number, timestamp_ms?: number): string | null {
    if (seq !== undefined) {
      if (seq <= this.lastSeq && this.lastSeq > 0) {
        // One line per run of drops: a short overlap after reattach is normal;
        // a run that never ends (seq far below lastSeq) is a lost epoch.
        if (!this._traceDedupRun) {
          this._traceDedupRun = true;
          toplog.log('pty', `dedup_drop shell=${this.shellId} seq=${seq} last_seq=${this.lastSeq}`);
        }
        return null;
      }
      this._traceDedupRun = false;
      this.lastSeq = seq;
    }
    let bytes: Uint8Array;
    let decoded: string;
    try {
      bytes = base64ToBytes(base64Data);
      decoded = this.decoder.decode(bytes, { stream: true });
    } catch (e) {
      console.warn('[PtyConnection] Failed to decode base64 PTY data:', e);
      return null;
    }
    if (seq !== undefined) {
      this.chunks.set(seq, { seq, data: bytes, timestamp: timestamp_ms ?? Date.now() });
      this._chunkBytes += bytes.length;
      this._trimChunks(seq);
    }
    // Feed line listeners regardless of attach state — triggers must fire
    // for orphan-flushed output too so early pattern detection works.
    this._feedLineBuffer(decoded);
    for (const fn of this._textListeners) {
      try {
        fn(decoded);
      } catch (e) {
        console.error('[PtyConnection] text listener error:', e);
      }
    }
    // Only fire live listeners once attach has completed.
    // Gate on _attached only (not isLive) so unit tests work without a WS.
    if (this._attached) {
      for (const listener of this._listeners) {
        try {
          listener(decoded, seq);
        } catch (e) {
          console.error('[PtyConnection] listener error:', e);
        }
      }
    }
    return decoded;
  }

  /**
   * Drop the oldest chunks beyond the window. Seqs are inserted in increasing order (the dedup
   * above refuses anything at or below lastSeq), so the Map's first key is the oldest. The chunk
   * that just arrived is never dropped: the output listener reads it back by seq.
   */
  private _trimChunks(newestSeq: number): void {
    while (
      this.chunks.size > PtyConnection.CHUNK_WINDOW_MAX_CHUNKS ||
      this._chunkBytes > PtyConnection.CHUNK_WINDOW_MAX_BYTES
    ) {
      const oldest = this.chunks.keys().next().value as number;
      if (oldest === newestSeq) break;
      this._chunkBytes -= this.chunks.get(oldest)!.data.length;
      this.chunks.delete(oldest);
      this.trimmedThroughSeq = oldest;
    }
  }

  /**
   * Let the whole replay window go — the program in this PTY ended and no view will replay it
   * (a restart attaches a new PTY, which `attach` clears for anyway). Only the window: the seq
   * counter, line buffer, event fires and every listener stay, so the shell reads the same as
   * before and the next attach dedups and routes as it always did.
   */
  releaseWindow(): void {
    if (this.lastSeq > 0) this.trimmedThroughSeq = this.lastSeq;
    this.chunks.clear();
    this._chunkBytes = 0;
  }

  // ── Raw text ──────────────────────────────────────────────────────────────

  /**
   * Subscribe to the decoded stream as it arrives — escapes included, attached or not. What a
   * reader of invisible markers needs (`Shell.runCommand`), where `onLine` has stripped them.
   * Returns an unsubscribe function.
   */
  onText(fn: PtyTextListener): () => void {
    this._textListeners.add(fn);
    return () => this._textListeners.delete(fn);
  }

  // ── Line stream ───────────────────────────────────────────────────────────

  /**
   * Subscribe to ANSI-stripped lines. Fires on every \n found in the raw
   * stream — replayed chunks included. Trailing partial line (no newline)
   * is buffered until the next chunk arrives.
   *
   * Returns an unsubscribe function.
   */
  onLine(fn: PtyLineListener): () => void {
    this._lineListeners.add(fn);
    return () => this._lineListeners.delete(fn);
  }

  /**
   * Register a regex trigger over the line stream. ``onMatch`` fires
   * with the matched line and the regex match groups.
   *
   * If chunks have already arrived (e.g. registration happens after a
   * tab-reload's replay completes), the new trigger is run against the
   * lines still in the replay window (not the whole session) so consumers
   * don't lose visibility of matches that fired pre-registration. Synthesized fires are flagged
   * ``duringReplay: true`` so the viewer can distinguish catchup hits
   * from live ones.
   *
   * Returns an unsubscribe function.
   */
  addTrigger(trigger: PtyEvent): () => void {
    this._triggers.add(trigger);
    if (this.chunks.size > 0) this._catchupTrigger(trigger);
    return () => this._triggers.delete(trigger);
  }

  /**
   * Walk the replay window's chunks in seq order, decode + ANSI-strip,
   * split on LF / CRLF / bare CR terminal-row boundaries, and run a single
   * trigger against each line. Used by
   * ``addTrigger`` to retroactively match history when a watcher
   * registers after replay has already drained chunks through the
   * line buffer.
   *
   * Independent of the live ``_lineBuffer``: this scans the persisted
   * chunk map without affecting the running stream.
   */
  private _catchupTrigger(trigger: PtyEvent): void {
    let buf = '';
    const seqs = Array.from(this.chunks.keys()).sort((a, b) => a - b);
    for (const seq of seqs) {
      const chunk = this.chunks.get(seq);
      if (!chunk?.data) continue;
      buf += this.decoder.decode(chunk.data, { stream: true });
    }
    buf += this.decoder.decode();
    const lines = buf.split(/\r\n|[\r\n]/);
    for (const raw of lines) {
      const line = stripAnsi(raw);
      if (!line) continue;
      const m = line.match(trigger.pattern);
      if (!m) continue;
      try {
        trigger.onMatch(line, m);
      } catch (e) {
        console.error('[PtyConnection] catchup trigger error:', e);
      }
      this._recordEventFire(trigger, line, m, /*duringReplay*/ true);
    }
  }

  /** Number of currently-registered ``PtyEvent`` watchers. */
  getRegisteredEventCount(): number {
    return this._triggers.size;
  }

  /** Snapshot of buffered fire records (oldest first). */
  getEventFires(): readonly PtyEventFire[] {
    return this._eventFires;
  }

  /** Subscribe to new fires. Returns an unsubscribe function. */
  onEventFire(fn: PtyEventFireListener): () => void {
    this._eventFireListeners.add(fn);
    return () => this._eventFireListeners.delete(fn);
  }

  /** Drop all buffered fires (does not affect listeners or watchers). */
  clearEventFires(): void {
    this._eventFires.length = 0;
  }

  private _feedLineBuffer(decoded: string): void {
    this._lineBuffer += decoded;
    while (this._lineBuffer.length > 0) {
      const cr = this._lineBuffer.indexOf('\r');
      const lf = this._lineBuffer.indexOf('\n');
      const boundary = cr === -1 ? lf : lf === -1 ? cr : Math.min(cr, lf);
      if (boundary === -1) return;

      let boundaryWidth = 1;
      if (this._lineBuffer[boundary] === '\r') {
        // A CR at the end of a chunk may be the first half of CRLF. Keep it
        // until the next chunk so CRLF emits exactly one row even when split.
        if (boundary === this._lineBuffer.length - 1) return;
        if (this._lineBuffer[boundary + 1] === '\n') boundaryWidth = 2;
      }

      const line = this._lineBuffer.slice(0, boundary);
      this._lineBuffer = this._lineBuffer.slice(boundary + boundaryWidth);
      this._emitLine(stripAnsi(line));
    }
  }

  private _emitLine(line: string): void {
    for (const fn of this._lineListeners) {
      try {
        fn(line);
      } catch (e) {
        console.error('[PtyConnection] line listener error:', e);
      }
    }
    for (const trig of this._triggers) {
      const m = line.match(trig.pattern);
      if (m) {
        try {
          trig.onMatch(line, m);
        } catch (e) {
          console.error('[PtyConnection] trigger error:', e);
        }
        this._recordEventFire(trig, line, m);
      }
    }
  }

  private _recordEventFire(
    trig: PtyEvent,
    line: string,
    match: RegExpMatchArray,
    duringReplay?: boolean,
  ): void {
    const fire: PtyEventFire = {
      id:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `fire-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      ts: Date.now(),
      patternSource: trig.pattern.toString(),
      label: trig.label,
      line,
      match: Array.from(match).slice(0, 8) as string[],
      duringReplay: duringReplay ?? !this._attached,
    };
    this._eventFires.push(fire);
    while (this._eventFires.length > PtyConnection.MAX_EVENT_FIRES) {
      this._eventFires.shift();
    }
    for (const fn of this._eventFireListeners) {
      try {
        fn(fire);
      } catch (e) {
        console.error('[PtyConnection] event-fire listener error:', e);
      }
    }
  }

  // ── Event subscriptions (xterm interface) ─────────────────────────────────

  /**
   * Subscribe to live PTY output. Only fires after isReady.
   * Returns an unsubscribe function.
   */
  onOutput(fn: PtyOutputListener): () => void {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  /** Unsubscribe a live output listener (legacy compat). */
  offOutput(fn: PtyOutputListener): void {
    this._listeners.delete(fn);
  }

  /**
   * Subscribe to the "ready" event — fires every time attach completes
   * (including after reconnects). Fires immediately if already ready.
   * Returns an unsubscribe function.
   */
  onReady(fn: () => void): () => void {
    this._readyListeners.add(fn);
    // Fire immediately if already attached (mount of an existing terminal).
    if (this._attached) fn();
    return () => this._readyListeners.delete(fn);
  }

  /**
   * Subscribe to the "disconnect" event — fires when the WS drops.
   * Returns an unsubscribe function.
   */
  onDisconnect(fn: () => void): () => void {
    this._disconnectListeners.add(fn);
    return () => this._disconnectListeners.delete(fn);
  }

  // ── I/O (xterm interface) ─────────────────────────────────────────────────

  /** Send keystrokes to the backend PTY. */
  async sendInput(data: string): Promise<void> {
    if (!this.isLive && !this._everReady) {
      if (this._typeahead.length + data.length <= PtyConnection.TYPEAHEAD_MAX) {
        if (!this._typeahead) toplog.log('pty', `input_typeahead shell=${this.shellId} started=${this.started}`);
        this._typeahead += data;
        return;
      }
    }
    if (!this.isLive) {
      if (!this._traceInputDropRun) {
        console.warn('[PtyConnection] sendInput: PTY not live');
        this._traceInputDropRun = true;
        toplog.log('pty', `input_dropped shell=${this.shellId} reason=not_live started=${this.started}`);
      }
      return;
    }
    this._traceInputDropRun = false;
    if (!this.computeNodeId) return;
    const { ActionInfo } = await import('../../models/index.js');
    const { dataManager } = await import('../../APIEntity.js');
    const action = new ActionInfo('terminal-command', 'compute_node', this.computeNodeId, 'POST');
    action.subpath = 'input';
    action.bodyParameters = { shell_id: this.shellId, data };
    try {
      await dataManager.callActionOverWS<any, any>(action);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('PTY session not found')) {
        toplog.log('pty', `input_dropped shell=${this.shellId} reason=session_not_found`);
        this.started = false;
        this._attached = false;
        this._attachedPtyId = null;
        this._emitDisconnect();
      } else {
        throw e;
      }
    }
  }

  /**
   * Size the PTY to the view on screen. Kept even when the PTY is not live yet:
   * the next attach asserts it. Always sent when live — the backend may hold
   * another client's size, so a client-side "unchanged" says nothing.
   *
   * `repaint`: the view drew output at another size — redraw even at an unchanged size.
   * Resolves false only when a live send failed.
   */
  async resize(cols: number, rows: number, opts: { repaint?: boolean } = {}): Promise<boolean> {
    this._viewSize = { cols, rows };
    if (!this.started) return true; // kept for the attach, which repaints
    if (!this.computeNodeId) return true;
    return this._sendResize(cols, rows, opts.repaint === true);
  }

  /** The view stopped showing this PTY: it no longer claims the size. */
  releaseViewSize(): void {
    this._viewSize = null;
  }

  /** The size the view on screen claims, or null when no view does. */
  get viewSize(): { cols: number; rows: number } | null {
    return this._viewSize;
  }

  private async _sendResize(cols: number, rows: number, repaint = false): Promise<boolean> {
    const { ActionInfo } = await import('../../models/index.js');
    const { dataManager } = await import('../../APIEntity.js');
    const action = new ActionInfo('terminal-command', 'compute_node', this.computeNodeId, 'POST');
    action.subpath = 'resize';
    action.bodyParameters = { shell_id: this.shellId, cols, rows, repaint };
    toplog.log('pty', `resize shell=${this.shellId} size=${cols}x${rows}${repaint ? ' repaint=drift' : ''}`);
    try {
      await dataManager.callActionOverWS<any, any>(action);
      return true;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toplog.log('pty', `resize_failed shell=${this.shellId} error=${msg}`);
      if (msg.includes('PTY session not found') || msg.includes('Failed to resize PTY')) {
        this.started = false;
      }
      return false;
    }
  }

  // ── Attach lifecycle (called by Shell) ────────────────────────────────────

  /**
   * Attach (or re-attach) to a backend PTY.
   * Idempotent and deduped — safe to call on every tab switch.
   *
   * No byte replay: the server asserts the client's size on the PTY and
   * forces the running TUI to repaint its live frame (winsize jiggle when
   * the size is unchanged). Pass cols/rows so the asserted size matches
   * this client's xterm.
   */
  async attach(
    ptyId: string,
    opts: { force?: boolean; timeout?: number; cols?: number; rows?: number } = {},
  ): Promise<void> {
    const { force = false, timeout } = opts;
    const targetPtyId = ptyId;

    if (!this.computeNodeId) return;

    if (force) this._doReset();

    if (this._attachPromise && this._attachingPtyId === targetPtyId && !force) {
      // Already attaching to this PTY — return the in-flight promise silently
      return this._attachPromise;
    }

    if (this.started && this._attached && this._attachedPtyId === targetPtyId && !force) {
      // Already attached — no-op (also avoids a redundant repaint jiggle)
      return;
    }

    const attachWork = (async () => {
      if (this._attachedPtyId !== targetPtyId) {
        this.clear();
        this._attached = false;
      }

      // The view's size when one claims it; never a guess. None → the backend
      // repaints at the PTY's current size.
      const cols = opts.cols ?? this._viewSize?.cols;
      const rows = opts.rows ?? this._viewSize?.rows;
      const t0 = performance.now();
      const ok = await this._reattach(targetPtyId, timeout, cols, rows);
      toplog.log(
        'pty',
        `attach shell=${this.shellId} pty=${targetPtyId} ok=${ok} force=${force} ms=${(performance.now() - t0).toFixed(0)}`,
      );
      if (!ok) {
        this._attached = false;
        this._attachedPtyId = null;
        throw new Error(`PTY ${targetPtyId} not found for shell ${this.shellId}`);
      }

      perfLog(`PtyConnection.attach() attached=true (shell=${this.shellId.slice(0, 8)})`);

      this._attachedPtyId = targetPtyId;
      this._attached = true;
      // A view that claimed (or changed) its size while the attach was in flight.
      const view = this._viewSize;
      if (view && (view.cols !== cols || view.rows !== rows)) void this._sendResize(view.cols, view.rows);
      // Remembered independently of attach state: a FAILED re-attach attempt
      // (not_found during the recovery gap) nulls _attachedPtyId, but the
      // self-healing hooks must still know what to re-attach when
      // on_recovered arrives.
      this._reattachPtyId = targetPtyId;
      this._wireReattachHooks();
      this._emitReady();
      this._everReady = true;
      void this._flushTypeahead();
    })();

    this._attachingPtyId = targetPtyId;
    // Compare against the stored promise, not `attachWork`: the field holds the
    // `.finally()` wrapper, so the old comparison never matched and a settled
    // attach was handed back to every later non-forced attach of the same id.
    const attachPromise: Promise<void> = attachWork.finally(() => {
      if (this._attachPromise === attachPromise) {
        this._attachPromise = null;
        this._attachingPtyId = null;
      }
    });
    this._attachPromise = attachPromise;
    return attachPromise;
  }

  /**
   * Forget the attach because the backend killed this PTY (→CLI switch).
   * The next →PTY respawn reuses the same pty id, so a stale `_attached`
   * turned its attach into a no-op and left the pane blank whenever the
   * terminal mounted too late to catch `restarted`. Local only: no request,
   * no repaint, no disconnect event.
   */
  markPtyGone(): void {
    this._attached = false;
    this._attachedPtyId = null;
  }

  // ── Self-healing membership (frozen-terminal RCA, bug A) ──────────────────
  //
  // Connection membership lives ONLY in the backend's in-memory PtyRegistry.
  // Park/resume preserves it across a transient WS drop of the SAME backend
  // process — but a RESTARTED backend has an empty registry, and the recovery
  // watchdog respawns workers with no attached connections. Route loaders
  // (the only other attach caller) run on mount only, so an already-open pane
  // stayed deaf forever. These hooks re-issue the attach whenever the
  // transport returns (on_reconnected) or the backend reports it revived our
  // worker (on_recovered). Re-attaching is idempotent — backend membership is
  // a set, and the repaint is a no-op frame refresh — so firing on a plain
  // park/resume cycle is harmless.

  /** Un-subscribers for the re-attach hooks; null until first attach. */
  private _reattachUnhooks: Array<() => void> | null = null;

  /** The PTY id the hooks should re-attach — survives failed attach attempts
   *  (which null _attachedPtyId). Set on every successful attach. */
  private _reattachPtyId: string | null = null;

  private _wireReattachHooks(): void {
    if (this._reattachUnhooks) return;
    this._reattachUnhooks = []; // claim synchronously; wiring completes async
    void import('../../websocket.js').then(({ ConnectionManager }) => {
      if (!this._reattachUnhooks) return; // disposed while the import resolved
      const connectionManager = ConnectionManager.getInstance();
      const reattach = () => {
        if (!this._reattachPtyId) return;
        void this.attach(this._reattachPtyId, { force: true }).catch(() => {
          // not_found while recovery is still in flight — the on_recovered
          // event will re-fire us once the worker is back.
        });
      };
      const onRecovered = (msg: { shell_id?: string }) => {
        if (msg?.shell_id === this.shellId) reattach();
      };
      connectionManager.on('on_reconnected', reattach);
      connectionManager.on('on_recovered', onRecovered);
      this._reattachUnhooks.push(
        () => connectionManager.off('on_reconnected', reattach),
        () => connectionManager.off('on_recovered', onRecovered),
      );
    });
  }

  // ── Fast cross-platform PTY ping ──────────────────────────────────────────

  async testPty(): Promise<boolean> {
    if (!this.started || !this.computeNodeId || !this.shellId) return false;
    const { ActionInfo } = await import('../../models/index.js');
    const { dataManager } = await import('../../APIEntity.js');
    const action = new ActionInfo('terminal-command', 'compute_node', this.computeNodeId, 'POST');
    action.subpath = 'ping';
    action.bodyParameters = { shell_id: this.shellId };
    try {
      const result = await dataManager.callActionOverWS<any, any>(action);
      return result?.data?.alive === true;
    } catch {
      return false;
    }
  }

  // ── Orphan buffer flush (called after _reattach succeeds) ─────────────────

  flush(entries: OrphanEntry[]): void {
    for (const entry of entries) {
      this.appendOutput(entry.data, entry.seq, entry.timestamp_ms);
    }
  }

  // ── Housekeeping ──────────────────────────────────────────────────────────

  /** Clear chunk buffer and reset seq counter (does NOT reset attach state).
   *  Also drops the PTY-event fire buffer — fires from a prior PTY pid are
   *  stale once we re-attach to a fresh pid. */
  clear(): void {
    this.chunks.clear();
    this._chunkBytes = 0;
    this.trimmedThroughSeq = 0;
    this.lastSeq = 0;
    this._lineBuffer = '';
    this._eventFires.length = 0;
  }

  dispose(): void {
    this._reattachUnhooks?.forEach((off) => off());
    this._reattachUnhooks = null;
    this._reattachPtyId = null;
    this._listeners.clear();
    this._readyListeners.clear();
    this._disconnectListeners.clear();
    this._lineListeners.clear();
    this._triggers.clear();
    this._eventFireListeners.clear();
    this._eventFires.length = 0;
    this.chunks.clear();
    this._chunkBytes = 0;
    this.trimmedThroughSeq = 0;
    this._lineBuffer = '';
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /** Upper bound on held typeahead — a paste larger than this before attach is refused as before. */
  static readonly TYPEAHEAD_MAX = 4096;

  /** Send what was typed before the first attach, in order, once the PTY is live. */
  private async _flushTypeahead(): Promise<void> {
    const pending = this._typeahead;
    if (!pending || !this.isLive) return; // kept for the next attach that comes up live
    this._typeahead = '';
    toplog.log('pty', `input_typeahead_flush shell=${this.shellId} chars=${pending.length}`);
    await this.sendInput(pending);
  }

  private _emitReady(): void {
    for (const fn of this._readyListeners) {
      try {
        fn();
      } catch (e) {
        console.error('[PtyConnection] onReady listener error:', e);
      }
    }
  }

  private _emitDisconnect(): void {
    for (const fn of this._disconnectListeners) {
      try {
        fn();
      } catch (e) {
        console.error('[PtyConnection] onDisconnect listener error:', e);
      }
    }
  }

  /** Reset all attach state for a force re-attach. */
  private _doReset(): void {
    toplog.log('pty', `reset shell=${this.shellId} chunks=${this.chunks.size} last_seq=${this.lastSeq}`);
    this.clear();
    this._attached = false;
    this._attachedPtyId = null;
    this._attachPromise = null;
    this._attachingPtyId = null;
    this._emitDisconnect();
  }

  private async _reattach(
    ptyId: string,
    timeout?: number,
    cols?: number,
    rows?: number,
  ): Promise<boolean> {
    if (!this.computeNodeId) return false;
    const { ActionInfo } = await import('../../models/index.js');
    const { dataManager } = await import('../../APIEntity.js');
    const { ConnectionManager } = await import('../../websocket.js');
    const connection_id = ConnectionManager.getInstance().id;
    const action = new ActionInfo('terminal-command', 'compute_node', this.computeNodeId, 'POST');
    action.subpath = 'attach';
    action.bodyParameters = {
      shell_id: this.shellId,
      pty_id: ptyId,
      connection_id,
      ...(cols !== undefined && rows !== undefined ? { cols, rows } : {}),
      // The hub records no pty-stream file: its replay buffer is the history,
      // sent on attach from the last seq this connection saw.
      ...(isHubOnly() ? { since_seq: this.lastSeq } : {}),
    };
    const result = await dataManager.callActionOverWS<any, any>(
      action,
      timeout !== undefined ? { timeout } : undefined,
    );

    if (result?.status === 'not_found') {
      this.started = false;
      return false;
    }

    this.started = true;

    // Flush any orphan chunks that arrived before attach() was called
    const { ptyOrphanBuffer } = await import('./ptyOrphanBuffer.js');
    ptyOrphanBuffer.flush(this.shellId, this);

    return true;
  }
}
